import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { notification } from 'antd';
import { DownloadIcon, RotateCwIcon } from 'lucide-react';

import { ProgramLoader } from '../reuse/SVG';
import { usePersistentState } from './hooks/usePersistentState';
import { useRegistrationReport } from './hooks/useRegistrationReport';
import { useRegistrationsTable } from './hooks/useRegistrationsTable';
import { usePinnedSummary } from './hooks/usePinnedSummary';
import { useSavedViews } from './hooks/useSavedViews';
import { useTableLayout } from './hooks/useTableLayout';
import { useTableShortcuts } from './hooks/useTableShortcuts';
import { columnDefsFor } from './components/table/columns';
import { Pagination } from './components/table/Pagination';
import { RegistrationsTable } from './components/table/RegistrationsTable';
import { RegistrationDrawer } from './components/drawer/RegistrationDrawer';
import { BulkActionBar } from './components/BulkActionBar';
import { PinnedSummary } from './components/summary/PinnedSummary';
import { SummaryCards } from './components/summary/SummaryCards';
import { ActiveFilters } from './components/toolbar/ActiveFilters';
import { RegistrationTabs } from './components/toolbar/RegistrationTabs';
import { TableToolbar } from './components/toolbar/TableToolbar';
import { ViewsMenu } from './components/toolbar/ViewsMenu';
import { answerPairCount, buildColumns } from './utils/columns';
import { pluralize } from './utils/format';
import { buildRegistrationsCsv, csvFilename, downloadCsv } from './utils/csv';
import { MAILTO_LIMIT, bccMailto, guardianEmails } from './utils/email';
import { resolveViewState, viewStateEquals } from './utils/views';
import '../counselor/staffApplication/staffApplication.generated.css';
import './reports.css';

/*
 * Director Reporting tab (docs/director-reporting-prototype.md), route /reports behind USE_REPORTING_TAB.
 *
 * PrivateRoute only checks that someone is logged in, so the page checks UserType itself: directors
 * (2) and super directors (11). A super director who has picked a camp already reads as 2 locally
 * (store/user selectCampAction); 11 is accepted too. Picking or switching camp reloads the app, and
 * everything below is keyed by campCode, so a switch starts from that camp's own data and settings.
 *
 * `reports-page` is the scope for reports.css. No <header> elements: main.css gives them a 113px
 * min-height.
 */

const ALLOWED_USER_TYPES = [2, 11];

const notify = (type, message, extra = {}) => notification[type]({ message, placement: 'bottomRight', duration: 3, ...extra });

// Clipboard copy of guardian addresses: the fallback when there's no mail app for mailto:, and the
// only way for long lists (mailto: links break above ~2,000 characters).
const copyEmails = async emails => {
  try {
    await navigator.clipboard.writeText(emails.join(', '));
    notify('success', `Copied ${emails.length} email ${emails.length === 1 ? 'address' : 'addresses'}`, { description: 'Paste them into the BCC field of a new email.' });
  } catch {
    notify('error', "Couldn't copy the addresses");
  }
};

// Season = programs starting in one calendar year. Next year, this year (default), two back,
// never before FIRST_SEASON.
const FIRST_SEASON = 2025;
const yearOptions = () => {
  const year = new Date().getFullYear();
  return [year + 1, year, year - 1, year - 2].filter(y => y >= FIRST_SEASON);
};

const RegistrationsReport = ({ userProfile }) => {
  const allowed = Boolean(userProfile) && ALLOWED_USER_TYPES.includes(Number(userProfile.UserType));
  // Empty for anyone redirected below, so nothing is loaded or stored for them.
  const campCode = allowed ? userProfile.campCode || '' : '';
  const years = yearOptions();
  const [storedYear, setYear] = usePersistentState(campCode ? `campsez.reports.${campCode}.season` : null, years[1]);
  const year = years.includes(storedYear) ? storedYear : years[1];

  const report = useRegistrationReport(campCode, year);
  const { camp, programs, rows } = report.data;
  // Question N / Answer N pairs: as many as the season's row with the most answers.
  const pairCount = useMemo(() => answerPairCount(rows), [rows]);
  const columns = useMemo(() => buildColumns(pairCount), [pairCount]);
  const t = useRegistrationsTable({ rows, programs, columns, campCode, dataKey: report.dataKey });
  const layout = useTableLayout(campCode, columns.order);
  const visibleColumns = useMemo(() => columnDefsFor(layout.visibleKeys), [layout.visibleKeys]);
  const { saveStatusNote } = report;
  // Resolves true once saved; the cell keeps the typed text open on false.
  const onSaveStatusNote = useCallback(
    async (row, text) => {
      const result = await saveStatusNote(row, text);
      if (result.ok) notify('success', result.statusNote ? 'Status note saved' : 'Status note cleared');
      else notify('error', "Couldn't save the status note", { description: result.error });
      return result.ok;
    },
    [saveStatusNote]
  );
  const ctx = useMemo(() => ({ density: layout.density, onOpen: t.setActiveId, onSaveStatusNote }), [layout.density, t.setActiveId, onSaveStatusNote]);
  const { refresh } = report;
  // Keeps the table, filters and selection; the rows swap in when the answer arrives.
  const refreshReport = useCallback(async () => {
    const result = await refresh();
    if (result.stale) return;
    if (result.ok) notify('success', 'Registrations refreshed');
    else notify('error', "Couldn't refresh the registrations", { description: result.error });
  }, [refresh]);
  const views = useSavedViews(campCode);
  const searchRef = useRef(null);
  const summaryRef = useRef(null);
  const pinned = usePinnedSummary(summaryRef);

  // Once the first data set is in, restore the filters of the last-used view (layout and sort are
  // stored already). Not when this browser tab already has its own filters.
  const restored = useRef(false);
  const { applyFilterState, setActiveId, fromSession } = t;
  const closeDrawer = useCallback(() => setActiveId(null), [setActiveId]);
  useEffect(() => {
    if (restored.current || report.loading || report.error) return;
    restored.current = true;
    if (!fromSession && views.activeView.id !== 'all') applyFilterState(resolveViewState(views.activeView, columns.order));
  }, [report.loading, report.error, fromSession, views.activeView, columns.order, applyFilterState]);

  useTableShortcuts({ searchRef, drawerOpen: Boolean(t.activeRegistration), onNext: t.openNext, onPrev: t.openPrev });

  const currentState = {
    tab: t.tab,
    search: t.search,
    columnFilters: t.columnFilters,
    textFilters: t.textFilters,
    sort: t.sort,
    groupBy: t.groupBy,
    density: layout.density,
    visibleKeys: layout.visibleKeys,
  };
  const isDirty = !viewStateEquals(resolveViewState(views.activeView, columns.order), currentState);

  const exportRows = (exportScope, list) => {
    if (!list.length) return;
    downloadCsv(buildRegistrationsCsv(list, layout.visibleKeys), csvFilename(campCode, year, exportScope));
    notify('success', `Exported ${pluralize(list.length, 'registration')}`);
  };

  // Selected rows in the order the table shows them; selected rows the current filters hide go last.
  const selectedInTableOrder = () => {
    const shown = t.orderedRows.filter(r => t.selectedIds.has(r.id));
    const shownIds = new Set(shown.map(r => r.id));
    return [...shown, ...t.selectedRows.filter(r => !shownIds.has(r.id))];
  };

  const emailSelected = () => {
    const emails = guardianEmails(t.selectedRows);
    if (!emails.length) {
      notify('info', 'No guardian email addresses on the selected registrations');
      return;
    }
    const href = bccMailto(emails);
    if (href.length > MAILTO_LIMIT) {
      copyEmails(emails);
      return;
    }
    window.location.href = href;
    const key = `reports-mailto-${Date.now()}`;
    notify('info', `Opening your email app with ${pluralize(emails.length, 'address', 'addresses')} in BCC`, {
      key,
      duration: 6,
      description: 'Nothing opened? Copy the addresses instead.',
      btn: (
        <button
          type="button"
          onClick={() => {
            notification.close(key);
            copyEmails(emails);
          }}
          className="ant-btn ant-btn-sm">
          Copy addresses
        </button>
      ),
    });
  };

  const applyView = view => {
    const state = resolveViewState(view, columns.order);
    t.applyViewState(state);
    layout.applyViewLayout(state);
    views.setActiveId(view.id);
    t.clearSelection();
  };

  if (!campCode) {
    return <Redirect to="/" />;
  }

  const campName = userProfile.campName || camp?.campName || '';
  const programCount = new Set(rows.map(r => r.programId)).size;

  return (
    <div className="body_container">
      <div className="reports-page tw-min-h-full tw-w-full tw-bg-sand-50 tw-font-sans tw-text-neutral-900">
        <div className="tw-mx-auto tw-max-w-[1440px] tw-px-4 tw-pb-28 tw-pt-8 sm:tw-px-6 lg:tw-px-10">
          <div className="tw-mb-8 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
            <div>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-500">
                {campName ? <span>{campName} ·</span> : null}
                <label htmlFor="reports-season" className="tw-sr-only">Season</label>
                <select
                  id="reports-season"
                  value={year}
                  onChange={e => setYear(Number(e.target.value))}
                  className="tw-h-8 tw-cursor-pointer tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-pl-2.5 tw-pr-7 tw-text-sm tw-text-neutral-900 focus:tw-border-brand-500 focus:tw-outline-none">
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <h1 className="tw-m-0 tw-mt-1 tw-text-[28px] tw-font-semibold tw-tracking-tight tw-text-neutral-900">Registrations</h1>
              {!report.loading && !report.error && rows.length ? (
                <p className="tw-mt-1 tw-text-sm tw-text-neutral-500">
                  {pluralize(rows.length, 'sign-up')} across {pluralize(programCount, 'program')}
                </p>
              ) : null}
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                onClick={refreshReport}
                disabled={report.loading || report.refreshing}
                title="Load the registrations again"
                className="tw-inline-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3.5 tw-text-sm tw-font-medium tw-text-neutral-900 tw-shadow-sm tw-transition-colors hover:tw-bg-sand-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50">
                <RotateCwIcon className={`tw-h-4 tw-w-4 tw-text-neutral-500 ${report.refreshing ? 'tw-animate-spin' : ''}`} aria-hidden />
                {report.refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                type="button"
                onClick={() => exportRows(t.isFiltered ? 'filtered' : 'all', t.orderedRows)}
                disabled={report.loading || !t.orderedRows.length}
                title={t.isFiltered ? `Export the ${t.orderedRows.length} filtered registrations` : 'Export all registrations'}
                className="tw-inline-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3.5 tw-text-sm tw-font-medium tw-text-neutral-900 tw-shadow-sm tw-transition-colors hover:tw-bg-sand-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50">
                <DownloadIcon className="tw-h-4 tw-w-4 tw-text-neutral-500" aria-hidden />
                Export CSV
              </button>
            </div>
          </div>

          {report.loading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-py-16" role="status" aria-label="Loading registrations">
              <ProgramLoader />
              <p className="tw-mt-2 tw-text-sm tw-text-neutral-500">Your data is loading. This may take 30 seconds to 1 minute.</p>
            </div>
          ) : report.error ? (
            <div className="tw-rounded-2xl tw-border tw-border-neutral-200 tw-bg-white tw-px-6 tw-py-12 tw-text-center tw-shadow-sm" role="alert">
              <p className="tw-text-sm tw-font-medium tw-text-neutral-900">Registrations couldn't be loaded.</p>
              <p className="tw-mt-1 tw-text-sm tw-text-neutral-500">{report.error}</p>
              <button
                type="button"
                onClick={report.reload}
                className="tw-mt-4 tw-inline-flex tw-h-9 tw-items-center tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3.5 tw-text-sm tw-font-medium tw-text-neutral-900 tw-shadow-sm hover:tw-bg-sand-50">
                Try again
              </button>
            </div>
          ) : !rows.length ? (
            <div className="tw-rounded-2xl tw-border tw-border-neutral-200 tw-bg-white tw-px-6 tw-py-12 tw-text-center tw-shadow-sm">
              <p className="tw-text-sm tw-font-medium tw-text-neutral-900">No registrations for {year}.</p>
              <p className="tw-mt-1 tw-text-sm tw-text-neutral-500">Nothing is booked yet for programs starting in {year}. Try another season.</p>
            </div>
          ) : (
            <>
              <PinnedSummary visible={pinned.visible} top={pinned.top} summary={t.summary} scope={t.scopeKind} />
              <div ref={summaryRef}>
                <SummaryCards
                  summary={t.summary}
                  scope={t.scopeKind}
                  onClearSelection={t.clearSelection}
                  onClearFilters={t.clearAllFilters}
                  onShowMissingDocuments={t.showMissingDocuments}
                />
              </div>

              <section
                aria-label="Registrations table"
                className="tw-mt-6 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-neutral-200 tw-bg-white tw-shadow-sm">
                <RegistrationTabs active={t.tab} counts={t.tabCounts} onChange={t.setTab} />
                <TableToolbar
                  viewsMenu={
                    <ViewsMenu
                      builtIn={views.builtIn}
                      custom={views.custom}
                      activeView={views.activeView}
                      isDirty={isDirty}
                      onApply={applyView}
                      onSaveNew={name => {
                        views.saveView(name, currentState);
                        notify('success', `Saved view “${name}”`);
                      }}
                      onUpdateActive={() => {
                        views.updateView(views.activeView.id, currentState);
                        notify('success', `Updated “${views.activeView.name}”`);
                      }}
                      onDelete={id => {
                        views.deleteView(id);
                        notify('info', 'View deleted');
                      }}
                    />
                  }
                  searchRef={searchRef}
                  search={t.search}
                  onSearchChange={t.setSearch}
                  resultCount={t.sortedRows.length}
                  groupBy={t.groupBy}
                  onGroupByChange={t.setGroupBy}
                  density={layout.density}
                  onDensityChange={layout.setDensity}
                  orderedKeys={layout.order}
                  visibleKeys={layout.visibleKeys}
                  meta={columns.meta}
                  isLayoutCustomized={layout.isCustomized}
                  onToggleColumn={layout.toggleColumn}
                  onShowAllColumns={layout.showAllColumns}
                  onHideAllColumns={layout.hideAllColumns}
                  onResetLayout={layout.resetLayout}
                />
                <ActiveFilters
                  columnFilters={t.columnFilters}
                  textFilters={t.textFilters}
                  meta={columns.meta}
                  onRemoveValues={k => t.setColumnFilter(k, [])}
                  onRemoveText={k => t.setTextFilter(k, '')}
                  onClearAll={t.clearAllFilters}
                />
                <RegistrationsTable
                  rows={t.pagedRows}
                  groups={t.groupBy !== 'none' ? t.pageGroups : null}
                  collapsedGroups={t.collapsedGroups}
                  onToggleGroup={t.toggleGroup}
                  columns={visibleColumns}
                  meta={columns.meta}
                  widths={layout.widths}
                  onResize={layout.resizeColumn}
                  onResetWidth={layout.resetColumnWidth}
                  onMoveColumn={layout.moveColumn}
                  ctx={ctx}
                  sort={t.sort}
                  onSort={t.setSort}
                  filterOptions={t.filterOptions}
                  columnFilters={t.columnFilters}
                  textFilters={t.textFilters}
                  onFilterChange={t.setColumnFilter}
                  onTextFilterChange={t.setTextFilter}
                  selectedIds={t.selectedIds}
                  onToggleRow={t.toggleRow}
                  onToggleAll={t.toggleAll}
                  allSelected={t.allFilteredSelected}
                  someSelected={t.someFilteredSelected}
                  density={layout.density}
                  activeId={t.activeRegistration?.id ?? null}
                  onClearFilters={t.clearAllFilters}
                />
                <BulkActionBar count={t.selectedIds.size} onEmail={emailSelected} onExport={() => exportRows('selected', selectedInTableOrder())} onClear={t.clearSelection} />
                <RegistrationDrawer
                  registration={t.activeRegistration}
                  onClose={closeDrawer}
                  onPrev={t.openPrev}
                  onNext={t.openNext}
                  onSaveStatusNote={onSaveStatusNote}
                  position={{ index: t.activeIndex, total: t.orderedRows.length }}
                />
                <div className="tw-border-t tw-border-neutral-200">
                  <Pagination
                    page={t.currentPage}
                    pageCount={t.pageCount}
                    pageSize={t.pageSize}
                    pageLength={t.pageLength}
                    total={t.orderedRows.length}
                    grouped={t.groupBy !== 'none'}
                    groupCount={t.groups.length}
                    onChange={t.setPage}
                    onPageSizeChange={t.setPageSize}
                  />
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  userProfile: state.user.userProfile,
});

export default connect(mapStateToProps)(RegistrationsReport);
